import instance from "api/cmsapi";

const EXCLUDED_DISTRICT_IDS = new Set(["EipJ1KBgsce", "EmVZbr0kApz"]);

export function isEligibleForQaRelease(status) {
  const normalized = String(status || "").trim();
  return normalized === "Pending" || normalized === "Reviewed";
}

export function isEligibleForClose(status) {
  return String(status || "").trim() === "Completed";
}

export function isEligibleForBulkStart(status, hasStartSnapshot = false) {
  const normalizedStatus = String(status || "").trim();
  return (
    (!normalizedStatus && !hasStartSnapshot) ||
    (normalizedStatus === "Start" && !hasStartSnapshot)
  );
}

export function buildStartAssessmentPayload({ districtId, year, user }) {
  const today = new Date().toISOString().split("T")[0];
  const username = user?.user?.username || "";
  const fullName = user?.user?.fullName || "";
  const userRole =
    user?.user?.userRoles?.find((role) =>
      ["DPAT TECHNICAL TEAM", "DPAT QUALITY ASSURANCE", "Superuser"].includes(role.name),
    )?.name?.replace(/\s+/g, "_")?.toUpperCase() || "DAQ";

  return {
    id: 0,
    username,
    fullName,
    userRole,
    type: "DPAT",
    districtId,
    year: Number(year),
    status: "Start",
    assessmentStartDate: today,
    assessmentEndDate: null,
    reviewStartDate: null,
    reviewEndDate: null,
    closedDate: null,
  };
}

export async function startDpatAssessment({ districtId, year, user }) {
  const payload = buildStartAssessmentPayload({ districtId, year, user });
  const response = await instance.post("assessments", payload);
  return response.data;
}

export function buildAssessmentStartCommentPayload({ districtId, year, user, tables }) {
  const commentDate = new Date().toISOString().split("T")[0];
  const assessmentPayload = buildStartAssessmentPayload({ districtId, year, user });

  return {
    id: 0,
    username: assessmentPayload.username,
    fullName: assessmentPayload.fullName,
    userRole: assessmentPayload.userRole,
    type: "DPAT",
    districtId,
    year: Number(year),
    tableCommented: "assessment_start_DAPT",
    comments: "Assessment started automatically with status: Start",
    gaps: "",
    commentDate,
    updateDate: commentDate,
    dddpDataDate: commentDate,
    dddpData: {
      indicator: "assessment_start_DAPT",
      tables,
    },
  };
}

export async function saveDpatAssessmentStartSnapshot({ districtId, year, user, tables }) {
  const payload = buildAssessmentStartCommentPayload({ districtId, year, user, tables });
  const response = await instance.post("comments", payload);
  return response.data;
}

export async function startDpatAssessmentWithSnapshot({
  row,
  year,
  user,
  tables,
}) {
  const assessment =
    String(row.status || "").trim() === "Start" && row.assessment?.id
      ? row.assessment
      : await startDpatAssessment({ districtId: row.districtId, year, user });

  const comment = await saveDpatAssessmentStartSnapshot({
    districtId: row.districtId,
    year,
    user,
    tables,
  });

  return { assessment, comment };
}

export function buildCompleteReviewPayload({ assessmentStatus, districtId, year, user }) {
  const today = new Date().toISOString().split("T")[0];
  const assessmentStartDate = assessmentStatus?.assessmentStartDate || today;
  const assessmentEndDate = assessmentStatus?.assessmentEndDate || today;

  return {
    id: assessmentStatus.id,
    username: assessmentStatus?.username || user?.user?.username || "",
    fullName: assessmentStatus?.fullName || user?.user?.fullName || "",
    userRole: assessmentStatus?.userRole || "DAQ",
    reviewedBy: assessmentStatus?.reviewedBy || user?.user?.username || "",
    completedBy: assessmentStatus?.completedBy || user?.user?.username || "",
    type: assessmentStatus?.type || "DPAT",
    districtId,
    year: Number(year),
    status: "Reviewed",
    assessmentStartDate,
    assessmentEndDate,
    reviewStartDate: assessmentStatus?.reviewStartDate || assessmentEndDate,
    reviewEndDate: today,
    closedDate: null,
  };
}

export function buildReleaseToDistrictPayload({ assessmentStatus, districtId, year, user }) {
  const payload = buildCompleteReviewPayload({ assessmentStatus, districtId, year, user });
  return {
    ...payload,
    status: "Completed",
    reviewEndDate: assessmentStatus?.reviewEndDate || payload.reviewEndDate,
  };
}

export async function completeDpatQaReview({ assessmentStatus, districtId, year, user }) {
  if (!assessmentStatus?.id) {
    throw new Error("No assessment ID found");
  }

  const payload = buildCompleteReviewPayload({ assessmentStatus, districtId, year, user });
  const response = await instance.put(`assessments/${payload.id}`, payload);
  return response.data;
}

export async function fetchDistrictDpatAssessments(districtRows, year, signal, onProgress) {
  const y = String(year);
  const rows = districtRows.filter((row) => !EXCLUDED_DISTRICT_IDS.has(row.districtId));
  const total = rows.length;

  const reportProgress = (loaded) => {
    if (!onProgress || signal?.aborted) return;
    const percent = total === 0 ? 100 : Math.min(100, Math.round((loaded / total) * 100));
    onProgress({ loaded, total, percent });
  };

  reportProgress(0);
  if (total === 0) return [];

  let snapshotDistrictIds = new Set();
  let assessmentsByDistrictId = new Map();

  const [snapshotResult, assessmentsResult] = await Promise.allSettled([
    instance.get(`comments/summary/year/${y}/DPAT/assessment_start_DAPT`, { signal }),
    instance.get(`assessments/districts/dpat/${y}/DPAT`, { signal }),
  ]);

  if (signal?.aborted) return [];

  if (snapshotResult.status === "fulfilled") {
    snapshotDistrictIds = new Set(
      (snapshotResult.value?.data || []).map((summary) => String(summary.districtId)),
    );
  }

  if (assessmentsResult.status === "fulfilled") {
    const assessments = Array.isArray(assessmentsResult.value?.data)
      ? assessmentsResult.value.data
      : [];
    assessmentsByDistrictId = new Map(
      assessments
        .filter((item) => item?.districtId != null)
        .map((item) => [String(item.districtId), item]),
    );
  }

  const results = rows.map((row) => {
    const assessment = assessmentsByDistrictId.get(String(row.districtId)) ?? null;
    const status = assessment?.status != null ? String(assessment.status).trim() : null;
    return {
      ...row,
      year: y,
      assessment,
      status,
      hasStartSnapshot: snapshotDistrictIds.has(String(row.districtId)),
    };
  });

  reportProgress(results.length);
  return results;
}



export function summarizeDpatAssessmentWorkflow(rows = []) {
  const counts = {
    none: 0,
    Start: 0,
    Pending: 0,
    Reviewed: 0,
    Completed: 0,
    Closed: 0,
    other: 0,
  };
  let qaReleaseEligible = 0;
  let bulkStartEligible = 0;

  const byRegion = {};

  for (const row of rows) {
    const raw = String(row.status || "").trim();
    let bucket = "none";
    if (!raw) {
      bucket = "none";
      counts.none += 1;
    } else if (raw === "Start") {
      bucket = "Start";
      counts.Start += 1;
    } else if (raw === "Pending") {
      bucket = "Pending";
      counts.Pending += 1;
    } else if (raw === "Reviewed") {
      bucket = "Reviewed";
      counts.Reviewed += 1;
    } else if (raw === "Completed") {
      bucket = "Completed";
      counts.Completed += 1;
    } else if (raw === "Closed") {
      bucket = "Closed";
      counts.Closed += 1;
    } else {
      bucket = "other";
      counts.other += 1;
    }

    if (isEligibleForBulkStart(raw, row.hasStartSnapshot)) bulkStartEligible += 1;
    if (isEligibleForQaRelease(raw)) qaReleaseEligible += 1;

    const regionKey = row.regionKey || "unknown";
    if (!byRegion[regionKey]) {
      byRegion[regionKey] = {
        regionKey,
        regionName: row.regionName || regionKey,
        total: 0,
        yetToStart: 0,
        start: 0,
        pending: 0,
        reviewed: 0,
        completed: 0,
        closed: 0,
      };
    }
    const region = byRegion[regionKey];
    region.total += 1;
    if (bucket === "none") region.yetToStart += 1;
    else if (bucket === "Start") region.start += 1;
    else if (bucket === "Pending") region.pending += 1;
    else if (bucket === "Reviewed") region.reviewed += 1;
    else if (bucket === "Completed") region.completed += 1;
    else if (bucket === "Closed") region.closed += 1;
  }

  const total = rows.length;
  const yetToStart = counts.none;
  const inProgress = counts.Start;
  const pendingReview = counts.Pending;
  const reviewed = counts.Reviewed;
  const completed = counts.Completed;
  const closed = counts.Closed;
  const startedCount = total - yetToStart;
  const pct = (value) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0);

  const statusPieChart = [
    { name: "Yet to start", value: yetToStart, key: "none" },
    { name: "Start", value: inProgress, key: "Start" },
    { name: "Pending", value: pendingReview, key: "Pending" },
    { name: "Reviewed", value: reviewed, key: "Reviewed" },
    { name: "Completed", value: completed, key: "Completed" },
    { name: "Closed", value: closed, key: "Closed" },
  ].filter((entry) => entry.value > 0);

  const pipelineBarChart = [
    { stage: "Yet to start", count: yetToStart, fillKey: "none" },
    { stage: "Start", count: inProgress, fillKey: "Start" },
    { stage: "Pending", count: pendingReview, fillKey: "Pending" },
    { stage: "Reviewed", count: reviewed, fillKey: "Reviewed" },
    { stage: "Completed", count: completed, fillKey: "Completed" },
    { stage: "Closed", count: closed, fillKey: "Closed" },
  ];

  const regionBreakdown = Object.values(byRegion)
    .map((region) => ({
      ...region,
      releasedPct: region.total > 0
        ? Math.round(((region.completed + region.closed) / region.total) * 1000) / 10
        : 0,
    }))
    .sort((a, b) => String(a.regionName).localeCompare(String(b.regionName)));

  return {
    counts,
    qaReleaseEligible,
    bulkStartEligible,
    total,
    yetToStart,
    inProgress,
    pendingReview,
    reviewed,
    completed,
    closed,
    startedCount,
    startedPct: pct(startedCount),
    completedPct: pct(completed + closed),
    pendingPct: pct(pendingReview + reviewed),
    statusPieChart,
    pipelineBarChart,
    regionBreakdown,
  };
}

export function formatAssessmentWorkflowStatus(status) {
  const raw = String(status || "").trim();
  return raw || "Yet to start";
}

export function hasReachedReviewedStatus(status) {
  const raw = String(status || "").trim();
  return raw === "Reviewed" || raw === "Completed" || raw === "Closed";
}

export function listDistrictsNotReviewed(rows = []) {
  return rows
    .filter((row) => !hasReachedReviewedStatus(row.status))
    .map((row) => ({
      districtId: row.districtId,
      districtName: row.districtName,
      regionName: row.regionName,
      status: formatAssessmentWorkflowStatus(row.status),
      rawStatus: String(row.status || "").trim() || "none",
    }))
    .sort(
      (a, b) =>
        String(a.regionName || "").localeCompare(String(b.regionName || "")) ||
        String(a.districtName || "").localeCompare(String(b.districtName || "")),
    );
}

export async function bulkReleaseAssessmentsToDistrict({
  rows,
  year,
  signal,
  onProgress,
  onRowReleased,
}) {
  const eligible = (rows || []).filter(
    (row) => isEligibleForQaRelease(row.status) && row.assessment?.id,
  );
  const total = eligible.length;

  const reportProgress = (processed, current = null) => {
    if (!onProgress || signal?.aborted) return;
    onProgress({ processed, total, current });
  };

  reportProgress(0, "Releasing all pending assessments…");

  if (total === 0) {
    return { succeeded: [], failed: [], total: 0 };
  }

  if (signal?.aborted) {
    return { succeeded: [], failed: [], total };
  }

  try {
    const response = await instance.get(`assessments/release/dpat/${year}`, { signal });
    reportProgress(total, null);

    const released = Array.isArray(response?.data) ? response.data : [];
    const succeeded = eligible.map((row) => {
      const updated = released.find(
        (item) => String(item?.districtId) === String(row.districtId),
      );
      return {
        ...row,
        assessment: updated || { ...row.assessment, status: "Completed" },
        status: updated?.status ?? "Completed",
      };
    });

    return { succeeded, failed: [], total };
  } catch (error) {
    if (signal?.aborted) {
      return { succeeded: [], failed: [], total };
    }

    const message = error?.response?.data?.message || error?.message || "Release failed";
    const failed = eligible.map((row) => ({ ...row, error: message }));
    reportProgress(total, null);
    return { succeeded: [], failed, total };
  }
}

export function buildCloseAssessmentPayload({ assessmentStatus, districtId, year, user }) {
  const today = new Date().toISOString().split("T")[0];
  return {
    id: assessmentStatus.id,
    username: assessmentStatus?.username || user?.user?.username || "",
    fullName: assessmentStatus?.fullName || user?.user?.fullName || "",
    completedBy: assessmentStatus?.completedBy || user?.user?.username || "",
    closedBy: assessmentStatus?.closedBy || user?.user?.username || "",
    userRole: assessmentStatus?.userRole || "DAQ",
    type: assessmentStatus?.type || "DPAT",
    districtId,
    year: Number(year),
    status: "Closed",
    assessmentStartDate: assessmentStatus?.assessmentStartDate || today,
    assessmentEndDate: assessmentStatus?.assessmentEndDate || today,
    reviewStartDate: assessmentStatus?.reviewStartDate || assessmentStatus?.assessmentEndDate || today,
    reviewEndDate: assessmentStatus?.reviewEndDate || today,
    closedDate: today,
  };
}

export async function bulkCloseAssessments({
  rows,
  year,
  user,
  signal,
  onProgress,
  onRowClosed,
}) {
  const eligible = (rows || []).filter(
    (row) => isEligibleForClose(row.status) && row.assessment?.id,
  );
  const total = eligible.length;
  const succeeded = [];
  const failed = [];

  const reportProgress = (processed, current = null) => {
    if (!onProgress || signal?.aborted) return;
    onProgress({ processed, total, current });
  };

  reportProgress(0, "Closing all released assessments…");

  if (total === 0) {
    return { succeeded, failed, total: 0 };
  }

  for (let i = 0; i < eligible.length; i += 1) {
    if (signal?.aborted) break;
    const row = eligible[i];
    reportProgress(i, row.districtName);
    try {
      const payload = buildCloseAssessmentPayload({
        assessmentStatus: row.assessment,
        districtId: row.districtId,
        year,
        user,
      });
      const response = await instance.put(`assessments/${payload.id}`, payload, { signal });
      const updated = {
        ...row,
        assessment: response?.data || { ...row.assessment, status: "Closed" },
        status: response?.data?.status ?? "Closed",
      };
      succeeded.push(updated);
      onRowClosed?.(updated);
    } catch (error) {
      if (signal?.aborted) break;
      failed.push({
        ...row,
        error: error?.response?.data?.message || error?.message || "Close failed",
      });
    }
  }

  reportProgress(succeeded.length + failed.length, null);
  return { succeeded, failed, total };
}
